import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import { type Ref, useState } from 'react';
import {
  type BlurEvent,
  type FocusEvent,
  TextInput as RNTextInput,
  type TextInputProps,
} from 'react-native';
import {
  StyleSheet,
  type UnistylesVariants,
  withUnistyles,
} from 'react-native-unistyles';

type ComponentProps = UnistylesVariants<typeof style>;

const UnistylesTextInput = withUnistyles(RNTextInput, (theme) => ({
  placeholderTextColor: theme.colors.typographySecondary,
}));

const UnistylesBottomSheetTextInput = withUnistyles(
  BottomSheetTextInput,
  (theme) => ({
    placeholderTextColor: theme.colors.typographySecondary,
  }),
);

export default function TextInput({
  ref,
  style: extraStyles,
  variant,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  isBottomSheet = false,
  ...rest
}: ComponentProps &
  TextInputProps & {
    ref?: Ref<RNTextInput>;
    isInvalid?: boolean;
    isBottomSheet?: boolean;
  }) {
  const [isFocused, setIsFocused] = useState(false);

  const onFocus = (event: FocusEvent) => {
    setIsFocused(true);
    if (onFocusProp) {
      onFocusProp(event);
    }
  };

  const onBlur = (event: BlurEvent) => {
    setIsFocused(false);
    if (onBlurProp) {
      onBlurProp(event);
    }
  };

  style.useVariants({
    variant,
  });

  const sharedProps = {
    style: [style.textInput(isFocused), extraStyles],
    ...rest,
    onFocus,
    onBlur,
  };

  if (isBottomSheet && process.env.EXPO_OS === 'native') {
    return (
      <UnistylesBottomSheetTextInput
        {...sharedProps}
        ref={
          ref
            ? (instance: RNTextInput | null | undefined) => {
                if (typeof ref === 'function') {
                  ref(instance ?? null);
                } else {
                  ref.current = instance ?? null;
                }
              }
            : undefined
        }
      />
    );
  }

  return <UnistylesTextInput {...sharedProps} ref={ref} />;
}

const style = StyleSheet.create((theme) => ({
  textInput: (isFocused: boolean) => ({
    color: theme.colors.typography,
    borderWidth: theme.size(0.5),
    borderRadius: theme.borderRadius.xl,
    borderColor: isFocused ? theme.colors.primary : theme.colors.borderMuted,
    paddingVertical: theme.size(2),
    paddingHorizontal: theme.size(3),
    fontSize: theme.fontSize.lg,
    variants: {
      variant: {
        primary: {
          _web: {
            outline: isFocused
              ? `${theme.size(0.25)}px solid ${theme.colors.primary}`
              : 'none',
            outlineOffset: 0,
          },
        },
        default: {
          _web: {
            outline: isFocused
              ? `${theme.size(0.25)}px solid ${theme.colors.primary}`
              : 'none',
            outlineOffset: 0,
          },
        },
      },
    },
  }),
}));
